import React from 'react';
import { Target, Users, Shield, Lightbulb, Compass } from 'lucide-react';
import { Sec, SH } from '../components/Shared';

const AboutPage = () => {
  return (
    <div className="af">
      <Sec py={160} bg="var(--grad-hero)">
        <div style={{ maxWidth: 840, margin: '0 auto', textAlign: 'center' }}>
          <div className="eyebrow au">Our Story</div>
          <h1 className="au" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px,5vw,64px)', fontWeight: 800, color: 'var(--on-surface)', lineHeight: 1, letterSpacing: '-.03em', marginBottom: 32 }}>Engineering a New <span className="gtext">Standard</span></h1>
          <p className="au" style={{ fontSize: 18, color: 'var(--on-surface-variant)', lineHeight: 1.7, marginBottom: 48, fontWeight: 500 }}>
            Founded in 2023, ICD Information Technology Club (ICDITC) was born from a simple observation: the gap between academic theory and industry reality was widening. We built a space where students could bridge that gap through radical peer-to-peer innovation.
          </p>
        </div>
      </Sec>

      <Sec py={120} bg="var(--surface-container-low)">
        <div className="g2">
          <div className="card au d1" style={{ padding: 48 }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(59,130,246,.08)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}><Target size={28} /></div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 16 }}>Our Mission</h3>
            <p style={{ fontSize: 16, color: 'var(--on-surface-variant)', lineHeight: 1.7 }}>
              To equip the students of Ideal College with the practical, modern and high-demand technical skills needed to excel in the global technology ecosystem, bridging the gap between theoretical knowledge and real-world application.
            </p>
          </div>
          <div className="card au d2" style={{ padding: 48 }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(139,92,246,.08)', color: 'var(--tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}><Lightbulb size={28} /></div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 16 }}>Our Vision</h3>
            <p style={{ fontSize: 16, color: 'var(--on-surface-variant)', lineHeight: 1.7 }}>
              To be the premier hub of student-led technical innovation, creating a culture where technology is accessible, collaborative and utilized to solve real problems.
            </p>
          </div>
        </div>
      </Sec>

      <Sec py={120}>
        <SH eyebrow="Values" title="Our Operational DNA" center={true} />
        <div className="g3">
          {[
            { icon: <Compass />, title: 'Hyper-Focus', sub: 'We prioritise depth over breadth. We would rather master one stack than be mediocre in ten.' },
            { icon: <Users />, title: 'Peer-To-Peer', sub: 'The best way to learn is to teach. Our club thrives on members mentoring each other.' },
            { icon: <Shield />, title: 'Open Protocol', sub: 'Everything we build is open. We believe in the power of public building and shared knowledge.' }
          ].map((v, i) => (
            <div key={i} className="card au" style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--surface-container-low)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>{v.icon}</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 16 }}>{v.title}</h3>
              <p style={{ fontSize: 14.5, color: 'var(--on-surface-variant)', lineHeight: 1.7 }}>{v.sub}</p>
            </div>
          ))}
        </div>
      </Sec>

      {/* MILESTONES SECTION */}
      <Sec py={120} bg="var(--on-surface)">
        <SH eyebrow="History" title="Evolution of ICDITC" sub="The evolution of the Information Technology Space" center={true} />
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 32 }}>
          {[
            { year: 'SEP 2023', title: 'Foundation', desc: 'The journey began with the official founding of the club, approved by the Ideal College administration to create a dedicated hub for tech enthusiasts.' },
            { year: 'NOV 2023', title: 'Inaugural Tech Event', desc: 'Successfully hosted our first event, establishing a foundation for technical skill-building and critical thinking within our community.' },
            { year: 'JUN 2024', title: 'IT Olympiad', desc: 'Reached a major milestone by organizing our 1st national-level event, bringing together talent from across the country to compete and innovate.' },
            { year: 'SEP 2025', title: '100+ Active Members & Beyond', desc: 'Celebrated the milestone of 100+ members by hosting the inaugural intra-college hackathon a high-stakes environment where members turned concepts into complete, functional projects.' }
          ].map((item, i) => (
            <div key={i} className="au" style={{ display: 'flex', gap: 24, animationDelay: `${i * 0.1}s` }}>
              <div style={{ flexShrink: 0, textAlign: 'right', width: 100 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, color: 'var(--primary)' }}>{item.year}</div>
              </div>
              <div style={{ width: 2, background: 'rgba(255,255,255,.1)', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 0, left: -4, width: 10, height: 10, borderRadius: '50%', background: 'var(--primary)', boxShadow: '0 0 10px var(--primary)' }} />
              </div>
              <div style={{ paddingBottom: 24 }}>
                <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 8 }}>{item.title}</h4>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,.6)', lineHeight: 1.6 }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Sec>

      <Sec py={120} bg="var(--surface-container-low)">
        <SH title="Meet Our Founders" sub="We are educators, engineers, entrepreneurs and dreamers dedicated to elevating Bangladesh's youth into the global spotlight." center={true} />

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 32 }}>
          {[
            { n: 'Md Abdul Aziz', r: 'Founder & Former President', i: 'https://membership.icditc.club/wp-content/uploads/ultimatemember/6/profile_photo.jpg?1779128296' },
            { n: 'Maruf Hossain Chowdhury', r: 'Co-Founder & Former Organizing Secretary', i: 'https://membership.icditc.club/wp-content/uploads/ultimatemember/54/profile_photo.jpg?1777228113' },
            { n: 'Azizur Rahman Rashed', r: 'Co-Founder & Former General Secretary', i: 'https://membership.icditc.club/wp-content/uploads/ultimatemember/59/profile_photo.jpeg?1777228297' },
            { n: 'Mahidul Islam', r: 'Co-Founder & Former Communication Secretary', i: 'https://card.taphex.com/wp-content/uploads/ultimatemember/2/profile_photo.png?1777228363' },
            { n: 'Niloy Chandra Sarkar', r: 'Co-Founder & Former Vice President', i: 'https://membership.icditc.club/wp-content/uploads/ultimatemember/58/profile_photo.jpg?1777228452' },
          ].map((m, idx) => {
            const isTextTop = idx % 2 === 0 && idx !== 4;
            const alignRight = idx === 4;
            return (
              <div key={idx} className={`au founder-card ${isTextTop ? 'text-top' : 'text-bottom'} ${alignRight ? 'align-right' : ''}`} style={{ flex: '0 0 calc((100% - 64px) / 3)', minWidth: 280, gap: 16 }}>
                <div className="founder-text">
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, color: 'var(--on-surface)', marginBottom: 4 }}>{m.n}</h3>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--slate-500)', letterSpacing: '.02em' }}>{m.r}</div>
                </div>
                <div style={{ minHeight: 360, background: '#f4f7f6', borderRadius: 4, overflow: 'hidden' }}>
                  <img src={m.i} alt={m.n} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              </div>
            );
          })}
        </div>
      </Sec>
    </div>
  );
};

export default AboutPage;
